import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Autocomplete,
    Box,
    Typography
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

const formatDate = (dateInput) => {
    if (!dateInput) return '';
    if (typeof dateInput === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
            return dateInput;
        }
        if (dateInput.includes('T')) {
            return dateInput.split('T')[0];
        }
        const d = new Date(dateInput);
        if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return dateInput;
    }
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        const year = dateInput.getFullYear();
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const day = String(dateInput.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    return dateInput;
};

export default function AssignShiftModal({ open, onClose, onSuccess, staffList = [], shiftList = [], initialStaffId = null, initialDate = null }) {
    const [formData, setFormData] = useState({
        staffId: '',
        shiftId: '',
        date: formatDate(new Date())
    });
    const [availableShifts, setAvailableShifts] = useState(shiftList);
    const [isLoadingShifts, setIsLoadingShifts] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const fetchShifts = async () => {
        setIsLoadingShifts(true);
        try {
            const data = await StaffService.getShifts({ noPagination: true });
            const list = Array.isArray(data) ? data : (data?.data || data?.shifts || []);
            setAvailableShifts(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Failed to fetch shifts in AssignShiftModal:', err);
        } finally {
            setIsLoadingShifts(false);
        }
    };

    useEffect(() => {
        if (open) {
            setFormData({
                staffId: initialStaffId || '',
                shiftId: '',
                date: formatDate(initialDate) || formatDate(new Date())
            });
            setError('');

            if (shiftList && shiftList.length > 0) {
                setAvailableShifts(shiftList);
            } else {
                fetchShifts();
            }
        }
    }, [open, initialStaffId, initialDate, shiftList]);

    const getDeptId = (item) => {
        if (!item) return '';
        return item.departmentId || item.department?.id || item.department?._id || item.department_id || '';
    };

    const getDeptName = (item) => {
        if (!item) return '';
        if (typeof item.department === 'string') return item.department;
        return item.department?.name || item.departmentName || '';
    };

    const checkDepartmentMatch = (staff, shift) => {
        if (!staff || !shift) return { matches: true };

        const shiftDeptId = getDeptId(shift);
        const shiftDeptName = getDeptName(shift);
        
        // If shift has no specific department or is for all departments, it matches any staff
        if (!shiftDeptId && (!shiftDeptName || shiftDeptName.toLowerCase() === 'all' || shiftDeptName.toLowerCase() === 'all departments')) {
            return { matches: true };
        }

        const staffDeptId = getDeptId(staff);
        const staffDeptName = getDeptName(staff);

        // If IDs match
        if (shiftDeptId && staffDeptId && String(shiftDeptId) === String(staffDeptId)) {
            return { matches: true };
        }

        // If names match
        if (shiftDeptName && staffDeptName && shiftDeptName.trim().toLowerCase() === staffDeptName.trim().toLowerCase()) {
            return { matches: true };
        }

        const staffLabel = staff.fullName || staff.name || 'Selected staff member';
        const staffDeptDisplay = staffDeptName || 'Unassigned';
        const shiftDeptDisplay = shiftDeptName || 'another department';

        return {
            matches: false,
            message: `Department mismatch: "${staffLabel}" is in "${staffDeptDisplay}", but shift "${shift.name}" is designated for "${shiftDeptDisplay}". Please choose a matching shift or staff member.`
        };
    };

    const activeStaffList = (staffList || []).filter(s => {
        if (initialStaffId && (s.id === initialStaffId || s._id === initialStaffId)) return true;
        const status = (s.status || 'Active').toLowerCase();
        return status === 'active';
    });

    const handleStaffChange = (newValue) => {
        const newStaffId = newValue ? (newValue.id || newValue._id) : '';
        setFormData(prev => ({ ...prev, staffId: newStaffId }));

        if (newStaffId && formData.shiftId) {
            const selectedStaff = activeStaffList.find(s => (s.id || s._id) === newStaffId);
            const selectedShift = availableShifts.find(sh => (sh.id || sh._id) === formData.shiftId);
            const match = checkDepartmentMatch(selectedStaff, selectedShift);
            if (!match.matches) {
                setError(match.message);
                setFormData(prev => ({ ...prev, shiftId: '' }));
            } else {
                setError('');
            }
        } else {
            setError('');
        }
    };

    const handleShiftChange = (newShiftId) => {
        setFormData(prev => ({ ...prev, shiftId: newShiftId }));

        if (newShiftId && formData.staffId) {
            const selectedStaff = activeStaffList.find(s => (s.id || s._id) === formData.staffId);
            const selectedShift = availableShifts.find(sh => (sh.id || sh._id) === newShiftId);
            const match = checkDepartmentMatch(selectedStaff, selectedShift);
            if (!match.matches) {
                setError(match.message);
            } else {
                setError('');
            }
        } else {
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (!formData.staffId || !formData.shiftId || !formData.date) {
                throw new Error('All fields are required.');
            }

            const selectedStaff = activeStaffList.find(s => (s.id || s._id) === formData.staffId);
            const selectedShift = availableShifts.find(sh => (sh.id || sh._id) === formData.shiftId);
            const match = checkDepartmentMatch(selectedStaff, selectedShift);
            if (!match.matches) {
                throw new Error(match.message);
            }

            const payload = {
                staffId: formData.staffId,
                shiftId: formData.shiftId,
                date: formatDate(formData.date)
            };

            await StaffService.assignShift(payload);
            onSuccess('Shift assigned successfully');
            onClose();
        } catch (err) {
            if (err.response?.status === 403 || err.response?.status === 405) {
                setError('Action not allowed while the hotel is suspended.');
            } else {
                setError(err.response?.data?.message || err.message || 'Failed to assign shift.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentSelectedStaff = activeStaffList.find(s => (s.id || s._id) === formData.staffId) || null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Assign Duty Shift</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    <Grid container spacing={2.5}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={activeStaffList}
                                getOptionLabel={(option) => {
                                    const deptName = getDeptName(option);
                                    return deptName ? `${option.fullName || option.name} (${deptName})` : (option.fullName || option.name || '');
                                }}
                                value={currentSelectedStaff}
                                onChange={(e, newValue) => handleStaffChange(newValue)}
                                disabled={!!initialStaffId}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Select Staff Member" 
                                        required 
                                        size="small"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" required error={!isLoadingShifts && availableShifts.length === 0}>
                                <InputLabel>Select Shift</InputLabel>
                                <Select
                                    value={formData.shiftId}
                                    onChange={(e) => handleShiftChange(e.target.value)}
                                    label="Select Shift"
                                    disabled={isLoadingShifts}
                                >
                                    {isLoadingShifts ? (
                                        <MenuItem disabled value="">
                                            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading shifts...
                                        </MenuItem>
                                    ) : availableShifts.length > 0 ? (
                                        availableShifts.map(shift => {
                                            const shiftDept = getDeptName(shift);
                                            const match = checkDepartmentMatch(currentSelectedStaff, shift);
                                            const isMismatch = currentSelectedStaff && !match.matches;

                                            return (
                                                <MenuItem 
                                                    key={shift.id || shift._id} 
                                                    value={shift.id || shift._id}
                                                    disabled={isMismatch}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                                        <span>{shift.name} ({shift.startTime} - {shift.endTime})</span>
                                                        {shiftDept && (
                                                            <Typography variant="caption" color={isMismatch ? "error" : "text.secondary"} sx={{ ml: 1, fontStyle: 'italic' }}>
                                                                {shiftDept}{isMismatch ? ' (Incompatible)' : ''}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })
                                    ) : (
                                        <MenuItem disabled value="">
                                            No shift templates available. Please configure shifts first.
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                label="Date"
                                type="date"
                                value={formData.date}
                                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                fullWidth
                                required
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                    <Button onClick={onClose} color="inherit">Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={16} />}
                        sx={{ px: 4 }}
                    >
                        {isSubmitting ? 'Assigning...' : 'Confirm Assignment'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
